<template>
    <div class="numeric-quarter-table__column">
        <span class="numeric-quarter-table__column-label text text--center text--h3">
            {{ t('quarter' + quarter) }}
        </span>

        <div class="numeric-quarter-table__column-row numeric-quarter-table__column-row--extra-padding">
            <div class="numeric-quarter-table__content">
                <span
                    class="numeric-quarter-table__content-text text text--blurry text--h2"
                    :class="{
                        'text--warning': quarterCompletion.warning,
                        'text--success': quarterCompletion.success,
                        'text--negative': quarterCompletion.negative,
                    }"
                >
                    {{ quarterCompletion.value }}

                    <component
                        class="numeric-quarter-table__content-icon numeric-quarter-table__content-icon--fluid svg svg--125"
                        :is="quarterCompletion.icon"
                    ></component>
                </span>
            </div>
        </div>

        <div class="numeric-quarter-table__column-row numeric-quarter-table__column-row--padding">
            <div class="numeric-quarter-table__content">
                <span class="numeric-quarter-table__content-label">
                    {{ t('numeric.expected-value') }}
                </span>

                <VInput
                    class="numeric-quarter-table__input input-field--primary input-field--text-center"
                    concurrency-mode
                    debounce-mode
                    :disabled="!canUpdatePrivateFields || disabled || committed"
                    :format-percent="viewMode === NumericViewMode.Percent"
                    :model-value="expectedValue"
                    numeric
                    :placeholder="t('default-placeholder', 0)"
                    round-decimals
                    @update:modelValue="$emit('update:expected-value', $event)"
                ></VInput>
            </div>

            <div class="numeric-quarter-table__content">
                <span class="numeric-quarter-table__content-label">
                    {{ t('numeric.actual-value') }}
                </span>

                <VInput
                    class="numeric-quarter-table__input input-field--primary input-field--text-center"
                    disabled
                    :format-percent="viewMode === NumericViewMode.Percent"
                    :model-value="actualValue"
                    numeric
                    :placeholder="t('default-placeholder', 0)"
                    round-decimals
                ></VInput>
            </div>
        </div>

        <div class="numeric-quarter-table__column-row">
            <template v-if="committed">
                <div class="numeric-quarter-table__content--button numeric-quarter-table__content--center">
                    <span class="numeric-quarter-table__content-text text text--h7 text--success">
                        <CompletedSvg class="svg svg--125"></CompletedSvg>

                        {{ t('numeric.value-committed') }}
                    </span>
                </div>
            </template>

            <template v-else>
                <VButton
                    class="button--primary button--rounded-no-padding button--green button--fluid button--center"
                    :disabled="!quarterCompletion.waiting || committed || !canUpdatePublicFields || disabled"
                    @click="$emit('commit-value')"
                >
                    {{ t('numeric.commit-value') }}
                </VButton>

                <VButton
                    class="button--primary button--square-225 button--rounded-no-padding button--center button--default"
                    :disabled="!quarterCompletion.waiting || !canUpdatePublicFields || committed || disabled"
                    @click="$emit('add-value')"
                >
                    <EditSvg></EditSvg>
                </VButton>
            </template>
        </div>
    </div>
</template>
<script setup lang="ts">
// Svg
import EditSvg from '../assets/edit.svg';
import CompletedSvg from '../assets/completed.svg';

// Components
import VInput from './VInput.vue';
import VButton from './VButton.vue';

// Other
import { useI18n } from 'vue-i18n';
import { defineProps, PropType } from 'vue';
import { INumericQuarterCompletion } from '@/mixins/NumericApi';
import QuarterNumber from '@/core/Values/QuarterNumber';
import NumericViewMode from '@/core/Values/NumericViewMode';

const { t } = useI18n();
defineProps({
    committed: { type: Boolean, required: true },
    quarter: { type: Number as PropType<QuarterNumber>, required: true },
    quarterCompletion: { type: Object as PropType<INumericQuarterCompletion>, required: true },
    actualValue: { type: Number, default: 0 },
    expectedValue: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    canUpdatePublicFields: { type: Boolean, default: false },
    canUpdatePrivateFields: { type: Boolean, default: false },
    viewMode: { type: Number as PropType<NumericViewMode>, default: NumericViewMode.Proportion },
});
</script>
<style lang="scss">
.numeric-quarter-table {
    &__column {
        max-width: 250px;
        padding: 0.5rem;
        border: 0.125rem dashed var(--background-tertiary);
        border-radius: 0.75rem;
    }

    &__column-row {
        display: flex;
        flex-wrap: nowrap;
        gap: 0.25rem;
        width: 100%;

        &--padding {
            padding-bottom: 0.375rem;
        }

        &--extra-padding {
            padding-bottom: 0.75rem;
        }
    }

    &__column-label {
        display: inline-block;
        width: 100%;
    }

    &__input {
        padding-top: 0.125px;
        padding-bottom: 0.125px;
    }

    &__content {
        width: 100%;

        & .input-field {
            background-color: var(--background-primary);
        }

        &--button {
            width: 100%;
            height: 2.25rem;
        }

        &--center {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    &__content-text {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__content-label {
        display: inline-block;
        width: 100%;

        @include caption-quaternary();
        text-align: center;
        color: var(--text-black-tertiary);
    }
}
</style>
